<template>
  <div
    class="d-block publication-category-item"
    @click.prevent="goToPublicationPage"
  >
    <div class="category-item-wrapper">
      <div
        class="category-item-cover"
        :class="{ 'cover-narrow-view': coverForNarrowView }"
      >
        <BookCover :book-id="publication.id" :publication="publication" />
        <PublicationProgress :publication-id="publication.id" />
      </div>
      <div class="category-item-text">
        <div class="category-item-top">
          <div class="category-item-top-line">
            <div class="category-item-name">
              <NuxtLink
                :to="toNuxtLink"
                :class="[publication.type.toLowerCase()]"
                @click="goToPublicationPage"
              >
                <span>{{ publication.name }}</span>
              </NuxtLink>
            </div>
          </div>
          <AuthorLinkControl
            :publication="publication"
            :is-link="showAuthorLink"
          />
          <div v-if="false" class="d-flex">
            <div
              v-if="categoryLabel || collectionCategory"
              class="category-item-book-category"
              :class="{
                'category-dot': publication.genres && publication.genres.length
              }"
              @click.prevent="goToCategoryPage"
            >
              <span v-if="categoryLabel">
                <NuxtLink :to="toNuxtCategoryLink()">
                  {{ getBookCategoryLabel(categoryLabel) }}
                </NuxtLink>
              </span>
              <span>
                <span v-if="!categoryLabel && collectionCategory">
                  <NuxtLink :to="toNuxtCategoryLink()">
                    {{ getBookCategoryLabel(collectionCategory) }}
                  </NuxtLink>
                </span>
              </span>
            </div>
            <div
              v-if="publication.genres && publication.genres.length"
              class="category-item-book-genres-wrap"
            >
              <span
                v-for="(genre, index) in publication.genres"
                :key="genre"
                :data-genres-left="publication.genres.length - index - 1"
                class="category-item-book-genre"
                @click.prevent="goToGenrePage(genre)"
              >
                <NuxtLink
                  :to="toNuxtGenreLink(genre)"
                  @click="goToGenrePage(genre)"
                >
                  {{ genre }}
                </NuxtLink>
              </span>
            </div>
          </div>
          <div v-if="false" class="category-item-category-wrap">
            <span v-if="collectionLabel" class="collection-label dot">{{
              collectionLabel
            }}</span>
          </div>
          <div class="category-item-stats">
            <div class="category-item-stats-side">
              <ManageFavouritesControl
                :publication-id="publication.id"
                icon
                color="primary"
                class="favourites-control-block"
                @click.native.prevent.stop
              />
              <div
                v-if="showLevel && publication.difficulty && false"
                class="category-item-difficulty dot"
              >
                <span>{{
                  $t('Library.book.item.level', {
                    level: publication.difficulty
                  })
                }}</span>
              </div>
              <div v-if="collectionItemsNumberLabel" class="dot">
                {{ collectionItemsNumberLabel }}
              </div>
              <TimeDuration
                :duration="publication.readingTime"
                dir="auto"
                class-name="dot"
              />
              <div v-if="publication.audio" class="icon-block dot">
                <BaseSpriteIcon icon-name="ico-headset-wire" />
              </div>
              <div v-if="isDownloaded" class="icon-block icon-primary dot">
                <BaseSpriteIcon
                  icon-name="ico-download-cloud"
                  custom-class="download-cloud"
                />
              </div>
            </div>
            <div class="category-item-stats-side"></div>
            <div v-if="showLevel && percents">{{ percents }}%</div>
          </div>
        </div>

        <div v-if="description && !wideDescription" class="item-description">
          {{ publication.shortDescription }}
        </div>
      </div>
    </div>
    <div v-if="wideDescription" class="item-description mt-2">
      {{ publication.shortDescription }}
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import publicationUtils from '@/services/utils/publicationUtils';

import BookCover from '@/components/base/BookCover/BookCover.vue';
import TimeDuration from '@/components/base/TimeDuration/TimeDuration.vue';
import BaseSpriteIcon from '@/components/base/BaseSpriteIcon/BaseSpriteIcon.vue';
import AuthorLinkControl from '@/components/controls/AuthorLinkControl/AuthorLinkControl.vue';
import PublicationProgress from '@/components/views/PublicationProgress/PublicationProgress.vue';
import ManageFavouritesControl from '@/components/controls/ManageFavouritesControl/ManageFavouritesControl.vue';

import PublicationsTypesEnum from '@shared/enums/PublicationsTypesEnum';

export default {
  name: 'PublicationCategoryItem',
  components: {
    BookCover,
    TimeDuration,
    BaseSpriteIcon,
    AuthorLinkControl,
    PublicationProgress,
    ManageFavouritesControl
  },
  props: {
    description: {
      type: Boolean,
      default: false
    },
    coverForNarrowView: {
      type: Boolean,
      default: false
    },
    wideDescription: {
      type: Boolean,
      default: false
    },
    publication: {
      type: Object,
      required: true
    },
    showCategories: {
      type: Boolean,
      default: false
    },
    showAuthorLink: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      toNuxtLink: publicationUtils.getPublicationLink(this.publication)
    };
  },
  computed: {
    ...mapGetters('ProgressStore', ['getPublicationProgress']),
    categoryLabel() {
      let label = '';
      if (this.publication.bookCategory || this.showCategories) {
        label = this.getBookCategoryLabel(
          this.publication.bookCategory || this.publication.category
        );
      }
      return label;
    },
    isCollection() {
      return this.publication.type === PublicationsTypesEnum.COLLECTION;
    },
    collectionItemsNumberLabel() {
      const items = this.publication.items || [];
      const numberBooks = items.length;
      return numberBooks
        ? this.$t('Library.collection.book.label', { numberBooks })
        : '';
    },
    collectionLabel() {
      return this.isCollection ? this.$t('Library.collection.label') : '';
    },
    collectionCategory() {
      return this.publication.collection ? this.publication.category : '';
    },
    showLevel() {
      return (
        this.$store.getters['ContextStore/showPublicationProgress'] &&
        this.publication.type !== PublicationsTypesEnum.COLLECTION
      );
    },
    percents() {
      const progress = this.getPublicationProgress(this.publication.id)
        .readPercent;
      return parseInt(progress);
    },
    isDownloaded() {
      return (
        this.publication.isContentDownloaded &&
        this.publication.isAudioDownloaded
      );
    },
    toCategoryLink() {
      return this.$store.getters['LibraryStore/toCategoryLink'](
        this.publication,
        true
      );
    }
  },
  methods: {
    goToCategoryPage() {
      this.$router.push(this.toCategoryLink);
    },
    goToGenrePage(genre) {
      this.$router.push(
        this.$store.getters['LibraryStore/toGenreLink'](this.publication, genre)
      );
    },
    getBookCategoryLabel(bookCategory) {
      return publicationUtils.getCategoryLocalizationKey(
        bookCategory,
        this.$t.bind(this)
      );
    },
    goToPublicationPage() {
      this.$router.push(publicationUtils.getPublicationLink(this.publication));
    },
    toNuxtGenreLink(genre) {
      return this.$store.getters['LibraryStore/toGenreLink'](
        this.publication,
        genre
      );
    },
    toNuxtCategoryLink() {
      return this.$store.getters['LibraryStore/toCategoryLink'](
        this.publication,
        true
      );
    }
  }
};
</script>

<style lang="less" src="./PublicationCategoryItem.less"></style>
